/**
 * Tous les montants de l'application sont des entiers de centimes.
 * Aucun flottant ne doit circuler : 0.1 + 0.2 n'a pas sa place dans un budget.
 */
export type Cents = number;

const eur = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
});

const eurNoDecimals = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

/** « 1 234,56 € ». Passer `round: true` pour les axes de graphique. */
export function formatEUR(cents: Cents, options?: { round?: boolean }): string {
  const formatter = options?.round === true ? eurNoDecimals : eur;
  return formatter.format(cents / 100);
}

/**
 * Tronque vers le bas, à l'euro.
 *
 * Un budget ne doit jamais annoncer plus qu'il n'y a : 3 255,90 € devient
 * 3 255 €, et non 3 256 €. Sur un solde négatif, le plancher creuse aussi —
 * c'est le même principe de prudence.
 */
export function floorToEuro(cents: Cents): Cents {
  return Math.floor(cents / 100) * 100;
}

/**
 * Répartit un montant en parts aussi égales que possible.
 *
 * La somme des parts est TOUJOURS égale au total : 10 € en trois donnent
 * 3,34 € + 3,33 € + 3,33 €, pas trois fois 3,33 € qui perdraient un centime.
 * Les centimes en trop vont aux premières parts.
 */
export function splitEvenly(totalCents: Cents, parts: number): Cents[] {
  if (parts <= 0) return [];

  const sign = totalCents < 0 ? -1 : 1;
  const absolute = Math.abs(totalCents);
  const base = Math.floor(absolute / parts);
  const remainder = absolute - base * parts;

  return Array.from({ length: parts }, (_, index) =>
    sign * (base + (index < remainder ? 1 : 0)),
  );
}

/** « +12,00 € » / « −12,00 € » — pour un écart, où le signe porte le sens. */
export function formatSignedEUR(cents: Cents, options?: { round?: boolean }): string {
  const formatted = formatEUR(Math.abs(cents), options);
  if (cents === 0) return formatted;
  return cents > 0 ? `+${formatted}` : `−${formatted}`;
}

/**
 * Lit une saisie utilisateur française et rend des centimes.
 * Accepte « 12,50 », « 12.50 », « 1 234,56 », « -3 », et arrondit au centime
 * au-delà de deux décimales. Rend `null` si la saisie n'est pas exploitable.
 */
export function parseAmountToCents(input: string): Cents | null {
  // \s couvre déjà l'espace insécable (U+00A0) et l'insécable étroit (U+202F),
  // que produisent le formatage français et certains claviers.
  const cleaned = input.replace(/\s/g, '').replace(',', '.');
  const match = /^([+-])?(\d*)(?:\.(\d*))?$/.exec(cleaned);
  if (match === null) return null;

  const [, sign, whole = '', frac = ''] = match;
  if (whole === '' && frac === '') return null;

  // On garde trois décimales pour arrondir sur la troisième, sans jamais
  // multiplier un flottant par 100.
  const fractionCents = frac === '' ? 0 : Math.round(Number(frac.padEnd(3, '0').slice(0, 3)) / 10);
  const total = (whole === '' ? 0 : Number(whole)) * 100 + fractionCents;

  if (!Number.isSafeInteger(total)) return null;
  return sign === '-' ? -total : total;
}
